import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
	IFilterValues,
	IRestaurantsContext,
	RestaurantsContext,
} from 'modules/CustomersAndRestaurants/Restaurants/Restaurants/restaurantsState/restaurantsContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { EntityStatus } from 'types';
import { createAppError } from 'utils/helpers';

const isEntityStatus = (value: string): value is EntityStatus =>
	Object.values(EntityStatus).some((entityStatus) => entityStatus === value);

const WithURLFilters: React.FC = ({ children }) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const {
		setFiltersFromQueryString,
		searchString,
		IDSelectedCustomer,
		IDSelectedCountry,
		IDSelectedCity,
		IDsSelectedClientType,
		status,
		sortBy,
		countOfItems,
		currentPage,
	} = useContext<IRestaurantsContext>(RestaurantsContext);
	const history = useHistory();
	const isMounted = useRef<boolean>(false);

	const queryParams = useMemo(() => history.location.search, [history.location.search]);

	const clientTypeSeparator = '+';
	const queryKeyValueSeparator = '=';
	const queryParamsSeparator = '&';

	const filterValues: { [key: string]: IFilterValues[keyof IFilterValues] } = useMemo(() => {
		return {
			searchString,
			IDSelectedCustomer,
			IDSelectedCountry,
			IDSelectedCity,
			IDsSelectedClientType: IDsSelectedClientType.join(clientTypeSeparator),
			status,
			sortBy,
			countOfItems,
			currentPage,
		};
	}, [
		searchString,
		IDSelectedCustomer,
		IDSelectedCountry,
		IDSelectedCity,
		IDsSelectedClientType,
		status,
		sortBy,
		countOfItems,
		currentPage,
	]);

	const getQueryStringFromState = () => {
		let resultedString = '?';

		for (const filter in filterValues) {
			const currentValue = `${filter}${queryKeyValueSeparator}${filterValues[filter]}${queryParamsSeparator}`;
			if (filterValues[filter]) {
				resultedString += currentValue;
			}
		}

		return resultedString.slice(0, resultedString.length - 1); // remove queryParamsSeparator at the end
	};

	const getStateFromQueryString = () => {
		const filters: IFilterValues = {
			searchString: '',
			IDSelectedCustomer: null,
			IDSelectedCountry: null,
			IDSelectedCity: null,
			IDsSelectedClientType: [],
			status: EntityStatus.Active,
			sortBy: '',
			countOfItems: '12',
			currentPage: 1,
		};

		const parseQueryStringToObject = () => {
			const properties = queryParams.slice(1).split(queryParamsSeparator);
			properties.forEach((el) => {
				const currentProperty = el.split(queryKeyValueSeparator);
				const propertyKey = currentProperty[0] as keyof IFilterValues;
				const propertyValue =
					typeof currentProperty[1] === 'string'
						? decodeURIComponent(currentProperty[1])
						: currentProperty[1];

				switch (propertyKey) {
					case 'currentPage':
						return (filters[propertyKey] = Number(propertyValue));
					case 'IDsSelectedClientType':
						return ((filters[propertyKey] as number[]) = [
							...propertyValue.split(clientTypeSeparator).map((el) => Number(el)),
						]);
					case 'status':
						return (filters[propertyKey] = isEntityStatus(propertyValue)
							? propertyValue
							: EntityStatus.Active);
					default:
						// @ts-ignore
						return (filters[propertyKey] = propertyValue);
				}
			});
		};

		try {
			parseQueryStringToObject();
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		}

		return filters;
	};

	// Set query string from state
	useEffect(() => {
		if (isMounted.current) {
			const queryStringFromState = getQueryStringFromState();
			history.push({
				search: queryStringFromState,
			});
		}
		isMounted.current = true;
	}, [filterValues, isMounted.current]);

	// Set state from query string
	useEffect(() => {
		if (queryParams) {
			const stateFromQueryString = getStateFromQueryString();
			setFiltersFromQueryString(stateFromQueryString);
		}
		isMounted.current = true;
	}, [queryParams]);

	return <>{children}</>;
};

export default WithURLFilters;
